import { useCallback } from 'react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { Prompt } from '../types/gotypes'

interface ListPromptsResponse {
  prompts: Prompt[]
  count: number
}

export const usePrompts = ({
  customer,
  promptType,
}: {
  customer: string
  promptType?: string
}) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const listQueryKey = ['prompts', { customer, promptType }]

  const listQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && account.user),
    queryFn: async () => {
      if (!customer || !account.user) return []
      try {
        const params = promptType ? `?prompt_type=${encodeURIComponent(promptType)}` : ''
        const res = await axios.get<ListPromptsResponse>(
          `${API_BASE_URL}/prompts/${customer}${params}`
        )
        return res.data.prompts
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Prompt, 'id' | 'created_at' | 'updated_at' | 'customer'>) => {
      const res = await axios.post<Prompt>(
        `${API_BASE_URL}/prompts/${customer}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts', { customer }] })
      snackbar.success('Prompt created')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<Prompt>) => {
      const res = await axios.put<Prompt>(
        `${API_BASE_URL}/prompts/${customer}/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts', { customer }] })
      snackbar.success('Prompt updated')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_BASE_URL}/prompts/${customer}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts', { customer }] })
      snackbar.success('Prompt deleted')
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const handleCreate = useCallback(
    async (data: Omit<Prompt, 'id' | 'created_at' | 'updated_at' | 'customer'>) => {
      return createMutation.mutateAsync(data)
    },
    [createMutation]
  )

  const handleUpdate = useCallback(
    async (id: string, data: Partial<Prompt>) => {
      return updateMutation.mutateAsync({ id, ...data })
    },
    [updateMutation]
  )

  const handleDelete = useCallback(
    async (id: string) => {
      return deleteMutation.mutateAsync(id)
    },
    [deleteMutation]
  )

  return {
    prompts: (listQuery.data || []) as Prompt[],
    promptsLoading: listQuery.isLoading,
    promptsError: listQuery.error?.message || null,
    refetchPrompts: () => listQuery.refetch(),

    createPrompt: handleCreate,
    updatePrompt: handleUpdate,
    deletePrompt: handleDelete,

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
